<template>
  <div :class="{ 'spinner': isLoading }">
    <div class="upcoming-meeting-layout" v-if="!isLoading">
      <div class="table-header-div">
        <div class="upcoming-meeting-heading-div">
          <div class="calendar-icon">
            <span class="material-symbols-outlined attendance-icon">event</span>
          </div>
          <div class="number-of-records-div" v-if="displayUniqueUpcomingMeetings.length > 0">
            <span class="tab-count">{{ displayUniqueUpcomingMeetings.length }}</span>
          </div>
          <h3 class="upcoming-meeting-div">Upcoming Meetings</h3>
        </div>
      </div>
      <div class="upcoming-meeting-table-div">
        <DataTable :value="displayUniqueUpcomingMeetings" :alwaysShowPaginator=false selectionMode="single" dataKey="id"
          :metaKeySelection="false" @rowSelect="onRowSelect" tableStyle="min-width: 10rem" :rows="7" paginator>
          <template #empty>
            <p class="no-records-class" v-if="!displayUniqueUpcomingMeetings.length && !isLoading">No Upcoming Meetings
            </p>
          </template>
          <Column field="sortableDateTime" header="DATE" sortable>
            <template #body="{ data }">
              {{ formatDate(data.calendar.date) }} {{ convertToUserTimezone(data) }}
            </template>
          </Column>
          <Column header="MEETING" sortable>
            <template #body="{ data }">
              <span>{{ data.calendar.tumor_board.tb_name }}</span>
            </template>
          </Column>
        </DataTable>
      </div>
    </div>
  </div>
</template>

<script setup>
// Copyright (c) 2020-2023 Omics Data Automation, Inc.
// Copyright (c) 2023 datma, inc.
//
// Proprietary and Confidential:
// Unauthorized copying of this file by any medium is strictly prohibited.

import { computed, defineEmits, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import moment from 'moment-timezone'
import { getScheduledPatients } from './getScheduledPatients.js'
import { getPatientDetailsByMRN } from './getPatientDetailsByMRN.js'
import { getRequestingPhysicians } from './getRequestingPhysicians.js'
import { mergeByMrn, mergeByPhysicianId } from '@/utils/utils.js'
import { formatDate } from '@/utils/utils.js'
import { getUniqueUpcomingValues, groupDataByObjectProperty } from '@/common/shared.js'

const store = useStore()
const scheduledPatients = ref([])
const scheduledPatientsFromStore = computed(() => store.state.session.scheduledPatients)
const userTimeZone = computed(() => store.getters.profile.timeZone)
const patientDetailsByMRN = ref([])
const requestingPhysician = ref([])
const upcomingMeetings = ref([])
const displayUniqueUpcomingMeetings = ref([])
const isLoading = ref(true)

const emit = defineEmits(['meeting-clicked'])

const convertToUserTimezone = (data) => {
  const date = data?.date || data.calendar.date
  const time = data?.start_time || data.calendar.start_time
  const timezone = data.calendar.tumor_board.tb_occurrence.timezone
  const sourceMoment = moment.tz(`${date} ${time}`, 'YYYY-MM-DD HH:mm', timezone)
  const userMoment = sourceMoment.tz(userTimeZone.value)

  return userMoment.format('h:mma')
}

onMounted(async () => {
  const scheduledPatientFromResponse = await getScheduledPatients()
  scheduledPatients.value = scheduledPatientFromResponse
  store.dispatch('setScheduledPatients', { scheduledPatients, publish: true })
})

watch(scheduledPatientsFromStore, async (newScheduledPatients) => {
  if (!newScheduledPatients) { return }

  const requestingPhysicianFromResponse = await getRequestingPhysicians()
  requestingPhysician.value = requestingPhysicianFromResponse

  const mrnList = newScheduledPatients && newScheduledPatients.map((item) => item.mrn)
  if (mrnList.length) {
    const patientDetailsByMRNFromResponse = await getPatientDetailsByMRN(mrnList)
    patientDetailsByMRN.value = patientDetailsByMRNFromResponse
  }
  const tableData = tableDataWithMRNPhysicianDetails()
  upcomingMeetings.value = groupDataByObjectProperty(tableData)
  displayUniqueUpcomingMeetings.value = getUniqueUpcomingValues(upcomingMeetings.value)

  // create sortable date time for sorting
  displayUniqueUpcomingMeetings.value.forEach(meeting => {
    const fullDateTimeString = `${meeting.date} ${meeting.start_time}`
    meeting.sortableDateTime = new Date(fullDateTimeString).getTime()
  })

  isLoading.value = false
  store.dispatch('setUpcomingMeetings', { upcomingMeetings, publish: true })
})

const onRowSelect = (meeting) => {
  store.dispatch('setSessionMeeting', { meeting, publish: true })
  emit('meeting-clicked')
}

const tableDataWithMRNPhysicianDetails = () => {
  let tableData = scheduledPatients.value
  if (scheduledPatients.value?.length && patientDetailsByMRN.value?.length) {
    tableData = mergeByMrn(scheduledPatients.value, patientDetailsByMRN.value)
  }
  if (scheduledPatients.value?.length && requestingPhysician.value?.length) {
    tableData = mergeByPhysicianId(tableData, requestingPhysician.value)
  }
  return tableData
}
</script>

<style lang="scss" scoped>
.table-header-div {
  display: flex;
  background-color: var(--White);
  align-items: center;
  border-top-right-radius: 15px;
  border-top-left-radius: 15px;
  margin-top: 1%;
}

.upcoming-meeting-heading-div {
  width: 100%;
  text-align: initial;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
}

.upcoming-meeting-div {
  font-size: 14px;
  font-weight: 600;
}

.upcoming-meeting-layout {
  border: 1px solid var(--Grey200);
  margin: 28px;
  border-radius: 6px;
  background-color: var(--White);
}

.upcoming-meeting-table-div {
  margin-bottom: 3%;
}

.no-records-class {
  color: var(--Black);
  font-size: 1.125rem;
  font-weight: 500;
  text-align: center;
}

.calendar-icon {
  margin-left: 15px;
}
</style>

<style>
.upcoming-meeting-table-div .p-column-header-content {
  font-size: 12px;
  font-weight: 500;
  color: var(--Grey600);
  font-family: 'Roboto';
}

.upcoming-meeting-table-div .p-datatable .p-datatable-tbody>tr>td {
  font-size: 14px;
  font-weight: 400;
  color: var(--Black);
  font-family: 'Roboto';
}
</style>
